/**
 * Frontend Error Handling Utilities
 * Implements error-based exception design pattern for React components
 */

import { useToastStore } from '../stores/toastStore';

/**
 * Base error class for all custom errors
 */
export class BaseError extends Error {
  constructor(
    public code: string,
    message: string,
    public details: Record<string, any> = {},
    public userMessage?: string
  ) {
    super(message);
    this.name = this.constructor.name;
    this.userMessage = userMessage || message;
    
    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert error to JSON for logging
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      details: this.details,
      timestamp: new Date().toISOString(),
      stack: this.stack
    };
  }
}

/**
 * Payment-related errors
 */
export class PaymentError extends BaseError {
  constructor(code: string, message: string, details: Record<string, any> = {}) {
    super(code, message, details);
    this.userMessage = this.getUserFriendlyMessage(code, message);
  }

  private getUserFriendlyMessage(code: string, message: string): string {
    const friendlyMessages: Record<string, string> = {
      'AUTH_REQUIRED': 'Please sign in to continue with your payment.',
      'PAYMENT_FAILED': 'Your payment could not be processed. Please try a different payment method.',
      'INSUFFICIENT_AMOUNT': 'The payment amount is below the minimum required deposit.',
      'INVALID_PROMO_CODE': 'The promo code you entered is not valid or has expired.',
      'RATE_LIMITED': 'Too many payment attempts. Please wait a moment before trying again.',
      'NETWORK_ERROR': 'Connection failed. Please check your internet connection and try again.',
      'SERVICE_UNAVAILABLE': 'Payment service is temporarily unavailable. Please try again in a few minutes.'
    };

    return friendlyMessages[code] || message;
  }
}

/**
 * Service-related errors
 */
export class ServiceError extends BaseError {
  constructor(code: string, message: string, details: Record<string, any> = {}) {
    super(code, message, details);
    this.userMessage = this.getUserFriendlyMessage(code, message);
  }

  private getUserFriendlyMessage(code: string, message: string): string {
    const friendlyMessages: Record<string, string> = {
      'SERVICE_NOT_FOUND': 'The requested service is not available.',
      'SERVICE_UNAVAILABLE': 'This service is temporarily unavailable. Please try again later.',
      'INVALID_RESPONSE': 'Unable to load service information. Please refresh the page.'
    };

    return friendlyMessages[code] || message;
  }
}

/**
 * Network-related errors
 */
export class NetworkError extends BaseError {
  constructor(code: string, message: string, details: Record<string, any> = {}) {
    super(code, message, details);
    this.userMessage = 'Connection failed. Please check your internet connection and try again.';
  }
}

/**
 * Validation errors
 */
export class ValidationError extends BaseError {
  constructor(code: string, message: string, details: Record<string, any> = {}) {
    super(code, message, details);
    this.userMessage = message; // Validation errors are usually user-friendly already
  }
}

/**
 * Error handler utility class
 */
export class ErrorHandler {
  /**
   * Wrap unknown errors in appropriate error types
   */
  static wrapError(error: any, fallbackMessage: string): BaseError {
    if (error instanceof BaseError) {
      return error;
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new NetworkError(
        'NETWORK_ERROR',
        'Network request failed',
        { originalError: error.message }
      );
    }

    return new PaymentError(
      'UNKNOWN_ERROR',
      fallbackMessage,
      { originalError: error.message }
    );
  }

  /**
   * Handle error with toast notification
   */
  static handleError(error: any, showToast: boolean = true): void {
    const wrappedError = this.wrapError(error, 'An unexpected error occurred');
    
    // Log error for debugging
    console.error('Error handled:', wrappedError.toJSON());

    // Show toast notification if requested
    if (showToast) {
      const { addToast } = useToastStore.getState();
      addToast({
        type: 'error',
        title: 'Error',
        message: wrappedError.userMessage,
        duration: 5000
      });
    }

    // Send error to monitoring service if configured
    this.sendToMonitoring(wrappedError);
  }

  /**
   * Handle error with custom UI feedback
   */
  static handleErrorWithCallback(
    error: any, 
    onError: (error: BaseError) => void
  ): void {
    const wrappedError = this.wrapError(error, 'An unexpected error occurred');
    
    // Log error
    console.error('Error handled with callback:', wrappedError.toJSON());
    
    // Call custom error handler
    onError(wrappedError);
    
    // Send to monitoring
    this.sendToMonitoring(wrappedError);
  }

  /**
   * Create error boundary handler for React components
   */
  static createErrorBoundaryHandler(componentName: string) {
    return (error: Error, errorInfo: any) => {
      const boundaryError = new BaseError(
        'COMPONENT_ERROR',
        `Error in ${componentName}`,
        { 
          componentStack: errorInfo.componentStack,
          originalError: error.message,
          stack: error.stack
        }
      );

      console.error(`React Error Boundary [${componentName}]:`, boundaryError.toJSON());
      this.sendToMonitoring(boundaryError);
    };
  }

  /**
   * Send error to monitoring service
   */
  private static sendToMonitoring(error: BaseError): void {
    // In a real implementation, this would send to Sentry, LogRocket, etc.
    if (import.meta.env.VITE_SENTRY_DSN) {
      // Example: Sentry.captureException(error);
    }

    // For now, just ensure it's logged
    if (import.meta.env.MODE === 'development') {
      console.group('🐛 Error Details');
      console.error('Code:', error.code);
      console.error('Message:', error.message);
      console.error('User Message:', error.userMessage);
      console.error('Details:', error.details);
      console.error('Stack:', error.stack);
      console.groupEnd();
    }
  }

  /**
   * Check if error is retryable
   */
  static isRetryable(error: BaseError): boolean {
    const retryableCodes = [
      'NETWORK_ERROR',
      'SERVICE_UNAVAILABLE',
      'RATE_LIMITED',
      'TIMEOUT'
    ];

    return retryableCodes.includes(error.code);
  }

  /**
   * Get retry delay for retryable errors
   */
  static getRetryDelay(error: BaseError, attempt: number): number {
    if (error.code === 'RATE_LIMITED') {
      // Use exponential backoff for rate limiting
      return Math.min(1000 * Math.pow(2, attempt), 30000);
    }

    if (error.code === 'NETWORK_ERROR') {
      // Linear backoff for network errors
      return Math.min(1000 * attempt, 10000);
    }

    // Default exponential backoff
    return Math.min(500 * Math.pow(2, attempt), 15000);
  }
}

/**
 * React hook for error handling
 */
export function useErrorHandler() {
  const handleError = (error: any, showToast: boolean = true) => {
    ErrorHandler.handleError(error, showToast);
  };

  const handleErrorWithCallback = (error: any, onError: (error: BaseError) => void) => {
    ErrorHandler.handleErrorWithCallback(error, onError);
  };

  const isRetryable = (error: BaseError) => {
    return ErrorHandler.isRetryable(error);
  };

  const getRetryDelay = (error: BaseError, attempt: number) => {
    return ErrorHandler.getRetryDelay(error, attempt);
  };

  return {
    handleError,
    handleErrorWithCallback,
    isRetryable,
    getRetryDelay
  };
}

/**
 * Async operation wrapper with error handling
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  errorMessage: string = 'Operation failed'
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw ErrorHandler.wrapError(error, errorMessage);
  }
}

/**
 * Retry wrapper for async operations
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  errorMessage: string = 'Operation failed after retries'
): Promise<T> {
  let lastError: BaseError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = ErrorHandler.wrapError(error, errorMessage);
      
      // Don't retry if error is not retryable or on last attempt
      if (!ErrorHandler.isRetryable(lastError) || attempt === maxAttempts) {
        throw lastError;
      }
      
      // Wait before retrying
      const delay = ErrorHandler.getRetryDelay(lastError, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.log(`🔄 Retrying operation (attempt ${attempt + 1}/${maxAttempts}) after ${delay}ms`);
    }
  }
  
  throw lastError!;
}

/**
 * Error boundary component props
 */
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: BaseError; retry: () => void }>;
  onError?: (error: BaseError) => void;
}

/**
 * Default error boundary fallback component
 */
export const DefaultErrorFallback: React.FC<{ error: BaseError; retry: () => void }> = ({ 
  error, 
  retry 
}) => (
  <div className="min-h-[200px] flex items-center justify-center p-6">
    <div className="text-center max-w-md">
      <div className="text-red-600 text-lg font-semibold mb-2">
        Something went wrong
      </div>
      <div className="text-gray-600 mb-4">
        {error.userMessage}
      </div>
      <button
        onClick={retry}
        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
      {import.meta.env.MODE === 'development' && (
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-sm text-gray-500">
            Error Details (Development)
          </summary>
          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
            {JSON.stringify(error.toJSON(), null, 2)}
          </pre>
        </details>
      )}
    </div>
  </div>
);